import { useCallback, useEffect, useMemo, useState } from "react";
import * as z from "zod";
import { useForm, Controller, useFieldArray } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";

import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";

import { restrictToVerticalAxis } from "@dnd-kit/modifiers";

import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  Autocomplete,
  CircularProgress,
  Divider,
  Tooltip,
  IconButton,
  InputBase,
} from "@mui/material";

import MuiAccordion, { AccordionProps } from "@mui/material/Accordion";
import MuiAccordionSummary, {
  AccordionSummaryProps,
} from "@mui/material/AccordionSummary";
import MuiAccordionDetails from "@mui/material/AccordionDetails";

import { styled } from "@mui/system";
import {
  Add,
  Delete,
  DragIndicator,
  ExpandMore,
  Save,
} from "@mui/icons-material";

import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { ptBR } from "date-fns/locale";
import {
  format,
  parse
} from "date-fns";

import {
  GenericInformationModal,
  GenericInformationModalProps,
} from "components/Modals/GenericInformationModal";
import { useSubscription } from "store/auth/auth-store";
import { useCreateReleaseNote } from "services/input-core/release-notes/create-release-notes";
import { toast } from "react-toastify";

const Title = styled(Typography)`
  font-size: 1.5rem;
  font-weight: 400;
  margin: 0;
  text-transform: uppercase;
`;

const Accordion = styled((props: AccordionProps) => (
  <MuiAccordion disableGutters elevation={0} square {...props} />
))(() => ({
  border: `2px solid #00000030`,
  borderRadius: "1rem",
  boxShadow: "2px 2px 2px rgb(0 0 0 / 20%)",
}));

const AccordionSummary = styled((props: AccordionSummaryProps) => (
  <MuiAccordionSummary {...props} />
))(() => ({
  backgroundColor: "rgba(255, 255, 255, 0.25)",
  cursor: "none",

  "& .MuiAccordionSummary-content": {
    width: "100%",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginRight: "0.5rem",
  },

  // Remove a estilização de foco do AccordionSummary
  "&.Mui-focusVisible": {
    outline: "none",
    boxShadow: "none",
    backgroundColor: "rgba(0, 0, 0, 0)",
  },
}));

const AccordionDetails = styled(MuiAccordionDetails)(({ theme }) => ({
  padding: theme.spacing(2),
  borderTop: "1px solid rgba(0, 0, 0, .125)",
}));

function CreateReleaseNotes() {
  const { t } = useTranslation([
    "release-notes",
    "list-messages",
    "list-errors",
  ]);

  const { subscriptionsAvailable } = useSubscription();

  const listSubscriptions = useMemo(() => {
    return subscriptionsAvailable.map((subs) => ({
      value: subs.subscriptionId,
      label: subs.name,
    }));
  }, [subscriptionsAvailable]);

  const [modals, setModals] = useState<GenericInformationModalProps>({
    modalOpen: false,
    modalTitle: "",
    modalDescription: "",
    modalType: "confirm-action",
    onAction: null,
  });

  const closeModal = () => {
    setModals(() => ({
      modalOpen: false,
      modalTitle: "",
      modalDescription: "",
      modalType: "confirm-action",
      onAction: null,
    }));
  };

  const openModal = (data: GenericInformationModalProps) => {
    setModals({
      modalOpen: true,
      ...data,
    });
  };

  const [expanded, setExpanded] = useState<string | false>(false);

  const handleChangeExpanded = (panel: string) => {
    return setExpanded((value) => (value === panel ? false : panel));
  };

  const languageTextSchema = z.object({
    type: z.string(),
    title: z
      .string({
        required_error: t("field-required"),
      })
      .min(1, { message: t("field-not-empty") }),
    description: z
      .string({
        required_error: t("field-required"),
      })
      .min(1, { message: t("field-not-empty") }),
  });

  const releaseNoteSchema = z.object({
    date: z
      .string({
        required_error: t("invalid-date"),
      })
      .min(1, { message: t("field-not-empty") }),
    fix: z.array(
      z.object({
        title: z.string({
          required_error: t("field-required"),
        }),
        order: z.number({
          required_error: t("field-required"),
        }),
        subscriptions: z
          .array(z.string(), {
            required_error: t("field-required"),
          })
          .min(1, {
            message: t("field-not-empty"),
          }),
        languageTexts: z.array(languageTextSchema).nonempty(),
      }),
    ),
    feature: z.array(
      z.object({
        title: z.string({
          required_error: t("field-required"),
        }),
        order: z.number({
          required_error: t("field-required"),
        }),
        subscriptions: z
          .array(z.string(), {
            required_error: t("field-required"),
          })
          .min(1, {
            message: t("field-not-empty"),
          }),
        languageTexts: z.array(languageTextSchema).nonempty(),
        link: z
          .string({
            required_error: t("field-required"),
          })
          .min(1, { message: t("field-not-empty") }),
      }),
    ),
  });

  // Tipagem para os campos do formulário
  type ReleaseNoteSchemaType = z.infer<typeof releaseNoteSchema>;

  type ReleaseNoteNames =
    | "date"
    | "fix" // array de objetos, não é um campo único
    | `fix.${number}.order`
    | `fix.${number}.title`
    | `fix.${number}.subscriptions`
    | `fix.${number}.languageTexts.${number}.title`
    | `fix.${number}.languageTexts.${number}.description`
    | "feature" // array de objetos, não é um campo único
    | `feature.${number}.order`
    | `feature.${number}.title`
    | `feature.${number}.subscriptions`
    | `feature.${number}.languageTexts.${number}.title`
    | `feature.${number}.languageTexts.${number}.description`
    | `feature.${number}.link`;

  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    reset,
  } = useForm<ReleaseNoteSchemaType>({
    resolver: zodResolver(releaseNoteSchema),
    defaultValues: {
      date: "",
      fix: [
        {
          title: "Fix 1",
          order: 0,
          subscriptions: [],
          languageTexts: [
            { type: "PT", title: "", description: "" },
            { type: "EN", title: "", description: "" },
            { type: "ITA", title: "", description: "" },
            { type: "ES", title: "", description: "" },
          ],
        },
      ],
      feature: [
        {
          title: "Feature 1",
          order: 0,
          subscriptions: [],
          link: "",
          languageTexts: [
            { type: "PT", title: "", description: "" },
            { type: "EN", title: "", description: "" },
            { type: "ITA", title: "", description: "" },
            { type: "ES", title: "", description: "" },
          ],
        },
      ],
    },
  });

  const {
    fields: fixFields,
    append: appendFix,
    replace: replaceFix,
    remove: removeFix,
  } = useFieldArray({
    control,
    name: "fix",
  });

  const {
    fields: featureFields,
    append: appendFeature,
    replace: replaceFeature,
    remove: removeFeature,
  } = useFieldArray({
    control,
    name: "feature",
  });

  const {
    mutate: setCreateReleaseNote,
    isLoading: isCreateReleaseNote,
  } = useCreateReleaseNote();

  function getValueByPath(obj: any, path: string): any {
    return path?.split(".")?.reduce((acc, key) => {
      // Verifica se a chave é um número, o que indica um índice de array
      const index = parseInt(key, 10);
      if (!isNaN(index)) {
        // Se for um número, tenta acessar o índice no array
        return acc?.[index];
      }
      // Caso contrário, acessa como uma propriedade normal
      return acc?.[key];
    }, obj);
  }

  const handleRemoveFix = (index: number) => {
    removeFix(index);

    const updatedFix = watch("fix")?.map((item, i) => ({
      ...item,
      order: i,
    }));

    replaceFix(updatedFix);
  };

  const handleRemoveFeature = (index: number) => {
    removeFeature(index);

    const updatedFeature = watch("feature")?.map((item, i) => ({
      ...item,
      order: i,
    }));

    replaceFeature(updatedFeature);
  };

  const onSubmit = (data: ReleaseNoteSchemaType) => {
    openModal({
      modalOpen: true,
      modalTitle: t(`create-release-note`),
      modalDescription: t(`confirm-create-release-note`),
      modalType: "confirm-action",
      onAction: () => {
        setCreateReleaseNote(
          {
            date: data?.date,
            feature: data?.feature?.map((feature) => ({
              order: feature?.order,
              subscriptions: feature?.subscriptions,
              link: feature?.link,
              languageTexts: feature?.languageTexts.map((lang) => ({
                type: lang.type,
                title: lang.title,
                description: lang.description,
              })),
            })),
            fix: data?.fix?.map((fix) => ({
              order: fix?.order,
              subscriptions: fix?.subscriptions,
              languageTexts: fix?.languageTexts.map((lang) => ({
                type: lang.type,
                title: lang.title,
                description: lang.description,
              })),
            })),
          },
          {
            onSuccess: () => {
              toast.success(
                t("success-create-release-note", {
                  ns: "list-messages",
                }),
              );
              reset();
              closeModal();
            },
            onError: () => {
              toast.error(
                t("errors-create-release-note", { ns: "list-errors" }),
              );
              closeModal();
            },
          },
        );
      },
    });
  };

  const renderInputBase = (
    name: ReleaseNoteNames,
    defaultValue: string,
    placeholder: string,
    required = false,
  ) => {
    return (
      <Box display="flex" flexDirection="column" width="100%">
        <Controller
          data-cy={`controller-${name}`}
          name={name}
          control={control}
          defaultValue={defaultValue}
          render={({ field }) => (
            <InputBase
              placeholder={placeholder}
              inputProps={{ "aria-label": "search google maps" }}
              sx={{ width: "100%" }}
              {...field}
              data-cy={`input-${name}`}
              id={name}
              required={required}
              style={{ width: "100%" }}
              tabIndex={0}
            />
          )}
        />

        {getValueByPath(errors, name) && (
          <Typography color="error" variant="caption" mt={1}>
            {getValueByPath(errors, name)?.message}
          </Typography>
        )}
      </Box>
    );
  };

  const renderTextField = (
    name: ReleaseNoteNames,
    label: string,
    required = false,
    type = "text",
    multiline = false,
    rows = 1,
    maxRows = 1,
    loading = false,
  ) => {
    return (
      <Box display="flex" flexDirection="column" width="100%">
        <Controller
          data-cy={`controller-${name}`}
          name={name}
          control={control}
          defaultValue=""
          render={({ field }) => (
            <TextField
              {...field}
              data-cy={`input-${name}`}
              id={name}
              variant="outlined"
              label={label}
              required={required}
              type={type}
              style={{ width: "100%" }}
              multiline={multiline}
              {...(multiline ? { minRows: rows, maxRows } : { rows })} // Condicional para definir as propriedades corretamente
              InputProps={{
                endAdornment: loading ? <CircularProgress size={20} /> : null,
                sx: { borderRadius: "0.5rem" },
              }}
              tabIndex={0}
            />
          )}
        />

        {getValueByPath(errors, name) && (
          <Typography color="error" variant="caption" mt={1}>
            {getValueByPath(errors, name)?.message}
          </Typography>
        )}
      </Box>
    );
  };

  const renderDatePicker = (
    name: "date",
    label: string,
    required: boolean,
    minDate?: Date,
    maxDate?: Date,
    disabled?: boolean,
  ) => {
    return (
      <Box display="flex" flexDirection="column" width="100%">
        <Controller
          name={name}
          control={control}
          rules={{ required }}
          render={({ field, fieldState: { error } }) => (
            <LocalizationProvider
              dateAdapter={AdapterDateFns}
              adapterLocale={ptBR}
            >
              <DatePicker
                {...field}
                data-cy={`date-picker-${name}`}
                label={label}
                defaultValue={null}
                minDate={minDate}
                maxDate={maxDate}
                disabled={disabled}
                shouldDisableDate={(day) =>
                  day?.valueOf() <= minDate?.valueOf() ? true : false
                }
                onChange={(date) => {
                  const dateString = date
                    ? format(date, "yyyy-MM-dd")
                    : "";
                  field.onChange(dateString);
                  setValue(name, dateString);
                }}
                value={field.value ? parse(field.value, "yyyy-MM-dd", new Date()) : null}
                slotProps={{
                  textField: {
                    error: !!error,
                    helperText: error ? t("field-required") : "",
                    tabIndex: 0,
                    sx: {
                      "& .MuiInputBase-root": {
                        borderRadius: "0.5rem",
                      },
                    },
                  },
                }}
              />
            </LocalizationProvider>
          )}
        />
      </Box>
    );
  };

  const renderAutocompleteField = (
    name: `fix.${number}.subscriptions` | `feature.${number}.subscriptions`,
    label: string,
    options: { value: string; label: string }[],
  ) => {
    return (
      <Box display="flex" flexDirection="column" width="100%">
        <Controller
          name={name}
          data-cy={`controller-${name}`}
          control={control}
          rules={{ required: true }}
          render={({ field }) => (
            <Autocomplete
              {...field}
              data-cy={`autocomplete-${name}`}
              id={name}
              key={name}
              limitTags={4}
              multiple
              options={options}
              getOptionLabel={(option: { value: string; label: string }) =>
                option?.label
              }
              defaultValue={
                Array.isArray(field.value)
                  ? options.filter((opt) => field.value.includes(opt.value))
                  : []
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  data-cy={`input-${name}`}
                  label={label}
                  error={Boolean(getValueByPath(errors, name))}
                  helperText={getValueByPath(errors, name)?.message}
                  tabIndex={0}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "0.5rem",
                    },
                  }}
                />
              )}
              onChange={(_, newValue) => {
                if (!newValue) {
                  setValue(name, []);
                } else {
                  const value = newValue.map(
                    (option: { value: string }) => option.value,
                  );
                  field.onChange(value);
                  setValue(name, value);
                }
              }}
              value={
                Array.isArray(field.value)
                  ? options.filter((option) =>
                      field.value.includes(option.value),
                    )
                  : []
              }
            />
          )}
        />
      </Box>
    );
  };

  const SortableAccordionItem = useCallback(
    ({
      id,
      index,
      type,
      field,
      expanded,
      handleChangeExpanded,
    }: {
      id: string;
      index: number;
      type: "fix" | "feature";
      field: any;
      expanded: string | false;
      handleChangeExpanded: (panel: string) => void;
    }) => {
      const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
      } = useSortable({ id });

      const style = {
        transform: CSS.Transform.toString(transform),
        transition,
      };

      return (
        <Box
          component={"div"}
          marginLeft={"1rem"}
          marginBottom={"2rem"}
          width={"100%"}
          ref={setNodeRef}
          style={style}
        >
          <Accordion expanded={expanded === `${type}-${index}`}>
            <AccordionSummary
              aria-controls={`panel${index}bh-content`}
              id={`panel${index}bh-header`}
              expandIcon={
                <IconButton
                  onClick={() => handleChangeExpanded(`${type}-${index}`)}
                >
                  <ExpandMore sx={{ fontSize: "2rem" }} />
                </IconButton>
              }
            >
              <Box
                display={"flex"}
                justifyContent={"flex-start"}
                alignItems={"center"}
                gap={1}
                width={"100%"}
              >
                {/* Aplica os listeners de arraste somente no ícone */}
                <IconButton
                  {...attributes}
                  {...listeners}
                  style={{ cursor: "move" }}
                >
                  <DragIndicator />
                </IconButton>

                <Box width={"50%"}>
                  {renderInputBase(
                    `${type}.${index}.title`,
                    field?.title,
                    field?.title,
                    false,
                  )}
                </Box>
              </Box>

              <Tooltip title={t("delete")}>
                <IconButton
                  onClick={() =>
                    openModal({
                      modalOpen: true,
                      modalTitle: t(`delete-${type}`),
                      modalDescription: t(`confirm-delete-${type}`),
                      modalType: "sensitive-action",
                      onAction: () => {
                        type === "fix"
                          ? handleRemoveFix(index)
                          : handleRemoveFeature(index);
                        closeModal();
                      },
                    })
                  }
                >
                  <Delete />
                </IconButton>
              </Tooltip>
            </AccordionSummary>
            {expanded === `${type}-${index}` && (
              <AccordionDetails>
                <Grid item xs={12}>
                  <Box
                    display={"flex"}
                    flexDirection={"column"}
                    gap={2}
                    margin={4}
                  >
                    {renderAutocompleteField(
                      `${type}.${index}.subscriptions`,
                      t(`subscriptions`),
                      listSubscriptions,
                    )}

                    {type === "feature" &&
                      renderTextField(
                        `feature.${index}.link`,
                        t(`redirection-link`),
                        false,
                        "text",
                        false,
                      )}

                    {/* Campos para cada languageTexts */}
                    <Grid container spacing={2}>
                      {field.languageTexts.map(
                        (lang: any, langIndex: number) => (
                          <Grid item xs={6} key={langIndex}>
                            <Box
                              display={"flex"}
                              flexDirection={"column"}
                              gap={2}
                              marginTop={1}
                            >
                              <label>{t(lang?.type)}</label>
                              {renderTextField(
                                `${type}.${index}.languageTexts.${langIndex}.title`,
                                t(`title`),
                                false,
                                "text",
                                false,
                              )}
                              {renderTextField(
                                `${type}.${index}.languageTexts.${langIndex}.description`,
                                t("description"),
                                false,
                                "text",
                                true,
                                5,
                                8,
                              )}
                            </Box>
                          </Grid>
                        ),
                      )}
                    </Grid>
                  </Box>
                </Grid>
              </AccordionDetails>
            )}
          </Accordion>
        </Box>
      );
    },
    [errors],
  );

  const sensors = useSensors(useSensor(PointerSensor));

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over) return;

    if (active.id !== over.id) {
      // Determine if the dragged item is from 'fix' or 'feature'
      const isActiveInFix = fixFields?.some((item) => item.id === active.id);
      const isOverInFix = fixFields?.some((item) => item.id === over.id);

      // Prevent moving 'fix' items into 'feature' and vice versa
      if (isActiveInFix !== isOverInFix) return;

      if (isActiveInFix) {
        const oldIndex = fixFields?.findIndex((item) => item.id === active.id);
        const newIndex = fixFields?.findIndex((item) => item.id === over.id);
        replaceFix(
          arrayMove(watch("fix"), oldIndex, newIndex).map((value, index) => ({
            ...value,
            order: index,
          })),
        );
      } else {
        const oldIndex = featureFields?.findIndex(
          (item) => item.id === active.id,
        );
        const newIndex = featureFields?.findIndex(
          (item) => item.id === over.id,
        );
        replaceFeature(
          arrayMove(watch("feature"), oldIndex, newIndex).map(
            (value, index) => ({
              ...value,
              order: index,
            }),
          ),
        );
      }
    }
  };

  useEffect(() => {
    if (errors?.fix) {
      const firstFixError = errors?.fix?.findIndex(
        (field) => field !== undefined,
      );

      if (firstFixError !== -1) {
        return setExpanded(`fix-${firstFixError}`);
      }
    }

    if (errors?.feature) {
      const firstFeatureError = errors?.feature?.findIndex(
        (field) => field !== undefined,
      );

      if (firstFeatureError !== -1) {
        return setExpanded(`feature-${firstFeatureError}`);
      }
    }
  }, [errors]);

  return (
    <>
      {modals.modalOpen && (
        <GenericInformationModal
          modalOpen={modals.modalOpen}
          modalTitle={modals.modalTitle}
          modalDescription={modals.modalDescription}
          modalType={modals.modalType}
          onClose={() => {
            closeModal();
          }}
          onAction={modals.onAction}
        />
      )}
      <Grid container>
        <Grid item xs={12}>
          <Box
            display="flex"
            flexDirection="column"
            gap={4}
            component={"form"}
            onSubmit={handleSubmit(onSubmit)}
          >
            {/* Cabeçalho e Botão Save */}
            <Box
              display="flex"
              alignItems="flex-start"
              justifyContent="space-between"
            >
              <Box minWidth={"25rem"}>
                {renderDatePicker("date", t("release-date"), true)}
              </Box>
              <Button
                variant="contained"
                color="success"
                startIcon={<Save />}
                size="large"
                type="submit"
                sx={{
                  borderRadius: "0.5rem",
                }}
                disabled={
                  (watch("fix")?.length <= 0 &&
                    watch("feature")?.length <= 0) ||
                  isCreateReleaseNote
                }
              >
                {t("save-release-note")}
              </Button>
            </Box>

            <Divider style={{ backgroundColor: "#808080" }} />

            {/* FIXS */}
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              marginBottom={"1rem"}
            >
              <Box minWidth={"25rem"}>
                <Title>{t("fix")}</Title>
              </Box>
              <Button
                variant="outlined"
                color="warning"
                startIcon={<Add />}
                size="large"
                sx={{
                  borderRadius: "0.5rem",
                }}
                onClick={() =>
                  appendFix({
                    title: `Fix ${fixFields.length + 1}`,
                    order: fixFields.length,
                    subscriptions: [],
                    languageTexts: [
                      { type: "PT", title: "", description: "" },
                      { type: "EN", title: "", description: "" },
                      { type: "ITA", title: "", description: "" },
                      { type: "ES", title: "", description: "" },
                    ],
                  })
                }
              >
                {t("fix")}
              </Button>
            </Box>

            {/* Campos para Fix */}
            <Grid container spacing={2}>
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                modifiers={[restrictToVerticalAxis]}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={fixFields}
                  strategy={verticalListSortingStrategy}
                >
                  {fixFields.map((field, index) => (
                    <SortableAccordionItem
                      key={`fix-${field.id}`}
                      id={field.id}
                      index={index}
                      field={field}
                      type="fix"
                      expanded={expanded}
                      handleChangeExpanded={handleChangeExpanded}
                    />
                  ))}
                </SortableContext>
              </DndContext>
            </Grid>

            <Divider style={{ backgroundColor: "#808080" }} />

            {/* FEATURES */}
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              marginBottom={"1rem"}
            >
              <Box minWidth={"25rem"}>
                <Title>{t("feature")}</Title>
              </Box>
              <Button
                variant="outlined"
                color="warning"
                startIcon={<Add />}
                size="large"
                sx={{
                  borderRadius: "0.5rem",
                }}
                onClick={() =>
                  appendFeature({
                    title: `Feature ${featureFields.length + 1}`,
                    order: featureFields.length,
                    subscriptions: [],
                    link: "",
                    languageTexts: [
                      { type: "PT", title: "", description: "" },
                      { type: "EN", title: "", description: "" },
                      { type: "ITA", title: "", description: "" },
                      { type: "ES", title: "", description: "" },
                    ],
                  })
                }
              >
                {t("feature")}
              </Button>
            </Box>

            {/* Campos para Feature */}
            <Grid container spacing={2}>
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                modifiers={[restrictToVerticalAxis]}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={featureFields}
                  strategy={verticalListSortingStrategy}
                >
                  {featureFields.map((field, index) => (
                    <SortableAccordionItem
                      key={`feature-${field.id}`}
                      id={field.id}
                      index={index}
                      field={field}
                      type="feature"
                      expanded={expanded}
                      handleChangeExpanded={handleChangeExpanded}
                    />
                  ))}
                </SortableContext>
              </DndContext>
            </Grid>
          </Box>
        </Grid>
      </Grid>
    </>
  );
}

export default CreateReleaseNotes;
